/**
 * VARMapTool Component
 *
 * Map tool for VAR (Values at Risk) operations:
 * - Toggle create mode and click-to-create VARs
 * - Export VAR data to CSV/GeoJSON
 *
 * Issue: #290
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faPlus,
  faFileExport,
  faFileCsv,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { VARCreateForm } from './VARCreateForm';
import { varServiceAdapter } from '../../../components/explorers/varExplorer/varServiceAdapter';
import { VARExportService, varExportService } from '../../../components/explorers/varExplorer/VARExportService';
import { CreateVARInput, VAREntity } from '../../../types/var';
import { LayerRegistry } from '../../layers2/LayerRegistry';
import { getBackendUrl } from '../../../services/configService';
import { logger } from '../../../utils/logger';
import { todayInNwt } from '../../../utils/nwtDate';
import './VARMapTool.scss';
import { useMessages } from '../../../components/common/AppMessages';

const log = logger('VARMapTool');

interface VARMapToolProps {
  map: mapboxgl.Map;
  hidden?: boolean;
}

export const VARMapTool: React.FC<VARMapToolProps> = ({ map, hidden }) => {
  const { showMessage } = useMessages();
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [clickLocation, setClickLocation] = useState<{ lon: number; lat: number } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Handle map click when in create mode
  const handleMapClick = useCallback((e: mapboxgl.MapMouseEvent) => {
    if (!isCreateMode) return;

    log.debug('Map clicked at', e.lngLat);
    setClickLocation({
      lon: e.lngLat.lng,
      lat: e.lngLat.lat
    });
  }, [isCreateMode]);

  // Set up/tear down map click handler
  useEffect(() => {
    if (isCreateMode) {
      map.on('click', handleMapClick);
      map.getCanvas().style.cursor = 'crosshair';
    } else {
      map.off('click', handleMapClick);
      map.getCanvas().style.cursor = '';
    }

    return () => {
      // Map / canvas can be torn down before this cleanup fires (e.g. when an
      // upstream React tree errors and unmounts everything). Guard so the
      // cleanup doesn't crash and mask the original error.
      if (!map || typeof map.off !== 'function') return;
      map.off('click', handleMapClick);
      const canvas = map.getCanvas?.();
      if (canvas) canvas.style.cursor = '';
    };
  }, [map, isCreateMode, handleMapClick]);

  // Listen for external VAR creation requests (e.g. from context menu)
  useEffect(() => {
    const handleExternalCreate = (e: Event) => {
      const { lat, lng } = (e as CustomEvent).detail;
      setClickLocation({ lon: lng, lat });
    };

    window.addEventListener('intellifire:var-create', handleExternalCreate);
    return () => {
      window.removeEventListener('intellifire:var-create', handleExternalCreate);
    };
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Toggle create mode
  const toggleCreateMode = useCallback(() => {
    setIsCreateMode(prev => !prev);
    setClickLocation(null);
  }, []);

  // Handle form submission
  const handleCreateSubmit = useCallback(async (data: CreateVARInput): Promise<string> => {
    log.debug('Creating VAR:', data);

    try {
      if (!varServiceAdapter.createEntity) {
        throw new Error('VAR creation not supported');
      }
      const created = await varServiceAdapter.createEntity(data as any);
      log.debug('VAR created successfully:', created.var_id);

      // Refresh VAR layer if it exists
      try {
        const registry = LayerRegistry.getInstance();
        const varLayers = registry.getByType('var');
        for (const layer of varLayers) {
          if (layer.isEnabled()) {
            await (layer as any).refresh?.();
          }
        }
      } catch (err) {
        log.warn('Could not refresh VAR layer:', err);
      }

      return created.var_id;
    } catch (error) {
      log.error('Failed to create VAR:', error);
      throw error;
    }
  }, []);

  const handleCreateDone = useCallback(() => {
    setClickLocation(null);
    setIsCreateMode(false);
  }, []);

  // Handle form cancel
  const handleCreateCancel = useCallback(() => {
    setClickLocation(null);
  }, []);

  // Fetch all VARs for export
  const fetchAllVARs = useCallback(async (): Promise<VAREntity[]> => {
    const backendUrl = getBackendUrl() || '';
    const response = await fetch(`${backendUrl}/api/var?limit=10000`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch VARs: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || [];
  }, []);

  // Export to CSV
  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    setShowExportMenu(false);

    try {
      const data = await fetchAllVARs();
      const blob = await varExportService.exportToCsv(data);
      const timestamp = todayInNwt();
      varExportService.download(blob, `var-export-${timestamp}.csv`);
      log.debug(`Exported ${data.length} VARs to CSV`);
    } catch (error) {
      log.error('CSV export failed:', error);
      showMessage('Failed to export CSV. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllVARs]);

  // Export to GeoJSON
  const handleExportGeoJson = useCallback(async () => {
    setIsExporting(true);
    setShowExportMenu(false);

    try {
      const data = await fetchAllVARs();
      const blob = await varExportService.exportToGeoJson(data);
      const timestamp = todayInNwt();
      varExportService.download(blob, `var-export-${timestamp}.geojson`);
      log.debug(`Exported ${data.length} VARs to GeoJSON`);
    } catch (error) {
      log.error('GeoJSON export failed:', error);
      showMessage('Failed to export GeoJSON. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllVARs]);

  return (
    <div className="var-map-tool" style={hidden ? { display: 'none' } : undefined}>
      {/* Create VAR button */}
      <button
        className={`var-tool-btn ${isCreateMode ? 'active' : ''}`}
        onClick={toggleCreateMode}
        title={isCreateMode ? 'Cancel create mode' : 'Create new VAR'}
      >
        <FontAwesomeIcon icon={faMapMarkerAlt} />
        <FontAwesomeIcon icon={faPlus} className="plus-icon" />
      </button>

      {/* Export dropdown */}
      <div className="export-dropdown" ref={exportMenuRef}>
        <button
          className={`var-tool-btn ${showExportMenu ? 'active' : ''}`}
          onClick={() => setShowExportMenu(prev => !prev)}
          disabled={isExporting}
          title="Export VAR data"
        >
          <FontAwesomeIcon icon={faFileExport} spin={isExporting} />
        </button>

        {showExportMenu && (
          <div className="export-menu">
            <button onClick={handleExportCsv} disabled={isExporting}>
              <FontAwesomeIcon icon={faFileCsv} />
              Export CSV
            </button>
            <button onClick={handleExportGeoJson} disabled={isExporting}>
              <FontAwesomeIcon icon={faGlobe} />
              Export GeoJSON
            </button>
          </div>
        )}
      </div>

      {/* Create form modal — portaled to body, renders even when hidden */}
      {clickLocation && (
        <VARCreateForm
          location={clickLocation}
          onSubmit={handleCreateSubmit}
          onDone={handleCreateDone}
          onCancel={handleCreateCancel}
        />
      )}
    </div>
  );
};

export default VARMapTool;
